import { Link } from 'react-router-dom';
import { PiArrowRightBold, PiCheckBold } from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { CommissionGoal, CommissionGoalBucketRow } from '@v2/pages/commissioning/types';
import { fmtBrlShort } from './homeFormat';

const BUCKET_COLOR: Record<string, string> = {
    force: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30',
    cardio: 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30',
    gluteleader: 'border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/30',
    accessories: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30',
};

type FocusKind = 'priority' | 'near' | 'hit';

function focusKind(pct: number): FocusKind {
    if (pct >= 1) return 'hit';
    if (pct >= 0.8) return 'near';
    return 'priority';
}

type Props = {
    goal: CommissionGoal | null;
    isLoading?: boolean;
    avgRate?: number | null;
};

export default function SellerFocusBuckets({ goal, isLoading, avgRate }: Props) {
    const { t } = useI18n();
    const buckets = [...(goal?.performance?.buckets ?? [])]
        .filter((b) => b.bucket !== 'other')
        .sort((a, b) => a.pct - b.pct);

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-800 dark:text-zinc-100">
                {t('v2.my_commission.focus_title')}
            </h2>

            {isLoading && <LoadingState variant="inline" className="py-8" />}

            {!isLoading && buckets.length === 0 && (
                <p className="py-6 text-center text-xs text-gray-400">{t('v2.my_commission.buckets_empty')}</p>
            )}

            {!isLoading && buckets.length > 0 && (
                <ul className="mt-3 space-y-2">
                    {buckets.map((b) => (
                        <FocusRow key={b.bucket} bucket={b} avgRate={avgRate} />
                    ))}
                </ul>
            )}

            <Link
                to="?view=full"
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-700 hover:underline dark:text-orange-400"
            >
                {t('v2.my_commission.focus_details_link')}
                <PiArrowRightBold className="size-3.5" />
            </Link>
        </section>
    );
}

function FocusRow({ bucket, avgRate }: { bucket: CommissionGoalBucketRow; avgRate?: number | null }) {
    const { t } = useI18n();
    const kind = focusKind(bucket.pct);
    const missing = Math.max(0, bucket.target - bucket.achieved);
    const estCommission = avgRate && avgRate > 0 && missing > 0
        ? missing * avgRate
        : null;

    const badge = kind === 'hit'
        ? t('v2.my_commission.focus_hit')
        : kind === 'near'
            ? t('v2.my_commission.focus_near')
            : t('v2.my_commission.focus_priority');

    return (
        <li className={cn('rounded-xl border px-3 py-2.5', BUCKET_COLOR[bucket.bucket] ?? 'border-gray-200 bg-gray-50')}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{bucket.label}</p>
                        <span className={cn(
                            'rounded-full px-2 py-0.5 text-[9px] font-bold uppercase',
                            kind === 'hit' && 'bg-emerald-200 text-emerald-900',
                            kind === 'near' && 'bg-amber-200 text-amber-900',
                            kind === 'priority' && 'bg-violet-200 text-violet-900',
                        )}>
                            {kind === 'hit' && <PiCheckBold className="mr-0.5 inline size-3" />}
                            {badge}
                        </span>
                    </div>
                    {kind === 'hit' ? (
                        <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-300">
                            {(bucket.pct * 100).toFixed(0)}% · {fmtBrlShort(bucket.achieved)}
                        </p>
                    ) : (
                        <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-300">
                            {(bucket.pct * 100).toFixed(0)}% · {t('v2.my_commission.focus_missing', {
                                amount: fmtBrlShort(missing),
                            })}
                            {estCommission != null && (
                                <span className="text-orange-700 dark:text-orange-300">
                                    {' '}· {t('v2.my_commission.focus_est_commission', {
                                        amount: fmtBrlShort(estCommission),
                                    })}
                                </span>
                            )}
                        </p>
                    )}
                </div>
                <p className="shrink-0 text-sm font-black tabular-nums text-gray-800 dark:text-zinc-100">
                    {(bucket.pct * 100).toFixed(0)}%
                </p>
            </div>
        </li>
    );
}
